import { useQuery } from '@tanstack/react-query';

import { apiClient, type PaginatedResult } from './api-client';

/**
 * AI assistant transcripts and reporting (spec §20, §63).
 *
 * Read-only. Nothing on the admin side writes to a conversation — a transcript
 * is a record of what was said, and an editable one would be worthless as
 * evidence of what the assistant told a buyer.
 */

export type ChatSessionStatus = 'ACTIVE' | 'ESCALATED' | 'CLOSED';
export type ChatRole = 'USER' | 'ASSISTANT' | 'SYSTEM';

export interface ChatSessionRow {
  id: string;
  sessionKey: string;
  status: ChatSessionStatus;
  customer: { id: string; customerCode: string; companyName: string } | null;
  messageCount: number;
  inquiryCount: number;
  startedAt: string;
  escalatedAt: string | null;
}

export interface ChatTranscriptMessage {
  id: string;
  role: ChatRole;
  content: string;
  /** Slugs the assistant looked up while answering. */
  productRefs: unknown;
  model: string | null;
  tokensUsed: number | null;
  wasAnswered: boolean | null;
  createdAt: string;
}

export interface ChatTranscript {
  id: string;
  sessionKey: string;
  status: ChatSessionStatus;
  customer: { id: string; customerCode: string; companyName: string } | null;
  startedAt: string;
  escalatedAt: string | null;
  inquiries: Array<{ id: string; inquiryNumber: string; status: string }>;
  feedback: Array<{ id: string; rating: number; comment: string | null; createdAt: string }>;
  messages: ChatTranscriptMessage[];
}

export interface UnansweredRow {
  id: string;
  sessionId: string;
  /** The user turn that preceded the fallback — the useful half. */
  question: string | null;
  reply: string;
  createdAt: string;
}

export interface AssistantSummary {
  sessions: number;
  escalated: number;
  aiInquiries: number;
  answered: number;
  unanswered: number;
  answerRatePercent: number;
}

const keys = {
  all: ['admin', 'assistant'] as const,
  summary: () => [...keys.all, 'summary'] as const,
  sessions: (params: { page?: number; search?: string }) =>
    [...keys.all, 'sessions', params] as const,
  transcript: (id: string) => [...keys.all, 'transcript', id] as const,
  unanswered: (params: { page?: number }) => [...keys.all, 'unanswered', params] as const,
};

export function useAssistantSummary() {
  return useQuery({
    queryKey: keys.summary(),
    queryFn: () => apiClient.get<AssistantSummary>('/admin/assistant/summary'),
  });
}

export function useChatSessions(params: { page?: number; limit?: number; search?: string }) {
  return useQuery({
    queryKey: keys.sessions(params),
    queryFn: () =>
      apiClient.get<PaginatedResult<ChatSessionRow>>('/admin/assistant/sessions', {
        query: { page: params.page, limit: params.limit, search: params.search || undefined },
      }),
    placeholderData: (previous) => previous,
  });
}

export function useChatTranscript(sessionId: string | null) {
  return useQuery({
    queryKey: keys.transcript(sessionId ?? ''),
    queryFn: () => apiClient.get<ChatTranscript>(`/admin/assistant/sessions/${sessionId}`),
    enabled: Boolean(sessionId),
  });
}

export function useUnansweredQuestions(params: { page?: number; limit?: number }) {
  return useQuery({
    queryKey: keys.unanswered(params),
    queryFn: () =>
      apiClient.get<PaginatedResult<UnansweredRow>>('/admin/assistant/unanswered', {
        query: { page: params.page, limit: params.limit },
      }),
    placeholderData: (previous) => previous,
  });
}

/** `productRefs` is a JSON column; normalise defensively rather than casting. */
export function readProductRefs(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];
}
